/*** IMPORTS FROM imports-loader ***/
import hydrator from "@gjensidige/views-api/src/runtime/hydrator";
import Component from ".";

/* global hydrator, Component */

// create a map of what we have hydrated, share it on window
window.__HYDRATED__ = window.__HYDRATED__ || {};

// get the current script executing
const partSrc = window.document?.currentScript?.getAttribute('src');

// filter the ones that need to be hydrated (data-nodeid contains the id for the props -> only hydration on node id's)
const parts = Array.from(
  document.querySelectorAll('script[src="' + partSrc + '"]')
).filter((part) => part.getAttribute('data-nodeid'));

// if this selection of parts contains elements that needs to be hydrated, add an eventListener
if (parts.length && window.addEventListener) {
  window.addEventListener('load', function () {
    parts.forEach((part) => {
      // make sure we only hydrate our components once
      const NODE_ID = part.getAttribute('data-nodeid');
      if (!window.__HYDRATED__[NODE_ID]) {
        window.__HYDRATED__[NODE_ID] = true;
        hydrator(Component, NODE_ID);
      }
    });
  });
}


import '@gjensidige/nci-core-typography/lib-css/index.css';
import '@gjensidige/nci-core-grid/lib-css/index.css';

import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types/prop-types';
import { Button } from '@gjensidige/core-components/lib/button/index';
import utils from '@gjensidige/core-utilities-css';
import { Col } from '@gjensidige/nci-core-grid/lib-css/col';
import { Container } from '@gjensidige/nci-core-grid/lib-css/container';
import { Row } from '@gjensidige/nci-core-grid/lib-css/row';
import { Title } from '@gjensidige/nci-core-typography/lib-css/title';

import { HeaderFeatureProps as Props } from './types';

import styles from '../less/header-feature.module.less';

export const HeaderFeature = ({
  title,
  image,
  buttons,
  mobileImage
}: Props) => {
  return (
    <Container
      as={'header'}
      fluid
      className={classNames(styles.overrides, styles.marginBHeaders)}
    >
      <Row>
        <Col md={12}>
          <Row className={classNames(utils['align-content-center'])}>
            {image && (
              <Col
                md={12}
                className={classNames(
                  utils['align-self-md-center'],
                  utils['pl-0'],
                  utils['pr-0'],
                  styles.photoWrapper
                )}
              >
                <img
                  src={image.url}
                  srcSet={image.srcset}
                  alt={image.alt ?? ''}
                  className={classNames(utils['p-0'], styles.photo)}
                  width={16}
                  height={9}
                  sizes={
                    image.srcset &&
                    `(min-width: 1024px) and (max-width: 1199px) 1024px,
                    (min-width: 1200px) and (max-width: 1619px) 1200px,
                    (min-width: 1620px) 1620px,
                    100vw`
                  }
                  loading="lazy"
                />
              </Col>
            )}
            {(mobileImage || image) && (
              <Col
                md={12}
                className={classNames(
                  utils['align-self-md-center'],
                  utils['pl-0'],
                  utils['pr-0'],
                  styles.mobilePhotoWrapper
                )}
              >
                <img
                  src={mobileImage?.url || image.url}
                  srcSet={mobileImage?.srcset || image.srcset}
                  alt={mobileImage?.alt || image.alt || ''}
                  className={classNames(utils['p-0'], styles.photo)}
                  width={16}
                  height={9}
                  sizes={
                    (mobileImage?.srcset || image.srcset) &&
                    `(min-width: 1024px) and (max-width: 1199px) 1024px,
                    (min-width: 1200px) and (max-width: 1619px) 1200px,
                    (min-width: 1620px) 2200px,
                    100vw`
                  }
                  loading="lazy"
                />
              </Col>
            )}
          </Row>
          <FloatingContent title={title} buttons={buttons} />
        </Col>
      </Row>
    </Container>
  );
};

HeaderFeature.propTypes = {
  title: PropTypes.string.isRequired,
  image: PropTypes.any.isRequired,
  mobileImage: PropTypes.any,
  buttons: PropTypes.array
};

HeaderFeature.CSR = true;

export default HeaderFeature;

const FloatingContent = ({ title, buttons }: Props) => {
  return (
    <Row className={classNames(styles.absoluteRow)}>
      <Container>
        <Row>
          <Col sm={12} md={8} lg={7}>
            <Row>
              <Col md={12}>
                {title && (
                  <Title tag="h1" className={styles.title} text={title} />
                )}
              </Col>
            </Row>
            {!!buttons?.length && (
              <Row>
                <Buttons buttons={buttons} />
              </Row>
            )}
          </Col>
        </Row>
      </Container>
    </Row>
  );
};

interface ButtonsProps {
  buttons: Props['buttons'];
}
const Buttons = ({ buttons }: ButtonsProps) => (
  <Col md="12" className={styles.buttonsWrapper}>
    {buttons.map((button, index) => (
      <Button
        key={`${button.text}_${index}`}
        variant={
          buttons.length === 1
            ? 'critical'
            : index === 0
            ? 'primary'
            : 'secondary'
        }
        data-theme="dark"
        href={button.url}
      >
        {button.text}
      </Button>
    ))}
  </Col>
);
